import { useEffect, useState, useCallback } from "react";
import { PortalCtx as Ctx, usePortal } from "@/lib/portalCtx";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import papi, { PTOKEN } from "@/lib/portalApi";
import { pp } from "@/lib/portalHost";
import InstallBanner from "@/pages/portail/InstallBanner";
import WelcomeDialog from "@/pages/portail/WelcomeDialog";
import { applyPortalPwa } from "@/pages/portail/portalPwa";
import { Loader2, LayoutDashboard, FolderKanban, GitCommitVertical, Ruler, FileText, FileSignature, ReceiptText, Wallet, Images, FolderOpen, MessageSquare, Bell, UserRound, LogOut, Menu, X } from "lucide-react";
import { PortalLogin, PortalAccess, PortalReset } from "@/pages/portail/PortalAuth";
import PortalDashboard from "@/pages/portail/PortalDashboard";
import { PortalProjects, PortalSection } from "@/pages/portail/PortalSections";
import { PortalMessages, PortalNotifications, PortalAccount } from "@/pages/portail/PortalMisc";

const MENU = [
  ["", "Tableau de bord", LayoutDashboard], ["projets", "Mes projets", FolderKanban], ["avancement", "Avancement", GitCommitVertical],
  ["plans", "Plans", Ruler], ["devis", "Devis", FileText], ["contrats", "Contrats", FileSignature], ["factures", "Factures", ReceiptText],
  ["paiements", "Paiements", Wallet], ["photos", "Photos & vidéos", Images], ["documents", "Documents", FolderOpen],
  ["messages", "Messages", MessageSquare], ["notifications", "Notifications", Bell], ["compte", "Mon compte", UserRound],
];

function Shell({ children }) {
  const { me, logout } = usePortal();
  const [open, setOpen] = useState(false);
  const nav = (
    <nav className="space-y-0.5">{MENU.map(([to, label, Icon]) => (
      <NavLink key={to} end to={pp(to ? `/${to}` : "")} onClick={() => setOpen(false)} data-testid={`portal-nav-${to || "dashboard"}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-white text-[#076633] font-semibold" : "text-emerald-50 hover:bg-white/10"}`}>
        <Icon className="h-4 w-4" /><span className="flex-1">{label}</span>
        {to === "notifications" && me.unread > 0 && <span className="rounded-full bg-amber-400 text-[#063d20] text-[10px] font-bold px-1.5" data-testid="portal-unread-badge">{me.unread}</span>}
      </NavLink>))}</nav>
  );
  return (
    <div className="min-h-screen bg-[#f4f7f5]">
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#076633] p-4 flex flex-col transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between mb-6"><img src="/logo-omnibat.png" alt="OMNIBAT" className="h-10 bg-white rounded-lg p-1.5" /><button className="lg:hidden text-white" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button></div>
        <div className="flex-1 overflow-y-auto">{nav}</div>
        <button onClick={logout} className="mt-4 flex items-center gap-2 text-sm text-emerald-100 hover:text-white px-3 py-2" data-testid="portal-logout"><LogOut className="h-4 w-4" />Déconnexion</button>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b px-4 sm:px-8 h-16 flex items-center gap-3">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="portal-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="flex-1 min-w-0"><p className="text-[11px] uppercase tracking-wider text-slate-400">Espace client</p><p className="font-semibold truncate" data-testid="portal-client-name">{me.client_name}</p></div>
          <NavLink to={pp("/notifications")} className="relative p-2" data-testid="portal-bell"><Bell className="h-5 w-5 text-slate-600" />{me.unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[10px] leading-4 text-center">{me.unread}</span>}</NavLink>
        </header>
        <main className="p-4 sm:p-8 max-w-6xl">{children}</main>
        <InstallBanner />
        <WelcomeDialog />
      </div>
    </div>
  );
}

function Guard() {
  const navg = useNavigate();
  const [me, setMe] = useState(null);
  const [projects, setProjects] = useState(null);
  const [pid, setPidState] = useState(localStorage.getItem("omnibat_client_pid") || "");
  const refreshMe = useCallback(() => papi.get("/me").then((r) => setMe(r.data)), []);
  useEffect(() => {
    if (!localStorage.getItem(PTOKEN)) { navg(pp("/login")); return; }
    refreshMe().catch(() => navg(pp("/login")));
    papi.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []); // eslint-disable-line
  if (!me || !projects) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#076633]" /></div>;
  const current = projects.find((p) => p.id === pid) || projects[0];
  const setPid = (v) => { localStorage.setItem("omnibat_client_pid", v); setPidState(v); };
  const logout = () => { localStorage.removeItem(PTOKEN); navg(pp("/login")); };
  return (
    <Ctx.Provider value={{ me, projects, current, setPid, logout, refreshMe }}>
      <Shell>
        <Routes>
          <Route index element={<PortalDashboard />} />
          <Route path="projets" element={<PortalProjects />} />
          <Route path="messages" element={<PortalMessages />} />
          <Route path="notifications" element={<PortalNotifications />} />
          <Route path="compte" element={<PortalAccount />} />
          <Route path=":section" element={<PortalSection />} />
        </Routes>
      </Shell>
    </Ctx.Provider>
  );
}

export default function PortalApp() {
  useEffect(applyPortalPwa, []);
  return (
    <Routes>
      <Route path="login" element={<PortalLogin />} />
      <Route path="access/:token" element={<PortalAccess />} />
      <Route path="reset/:token" element={<PortalReset />} />
      <Route path="*" element={<Guard />} />
    </Routes>
  );
}
